import { useRouter } from 'next/router';
import { useRef, useEffect } from 'react';

export default function TermsPrivacyView({ onClose, onNavigate }) {
  const router = useRouter();
  const viewRef = useRef(null);
  
  // Position the view when it's mounted
  useEffect(() => {
    const positionView = () => {
      // Find position of the trigger
      const termsPrivacyTrigger = document.querySelector('[data-terms-privacy-trigger]');
      if (termsPrivacyTrigger && viewRef.current) {
        const rect = termsPrivacyTrigger.getBoundingClientRect();
        
        // Wait for the view to be fully rendered to get its height
        setTimeout(() => {
          if (viewRef.current) {
            // Get the height of the view
            const viewHeight = viewRef.current.offsetHeight;
            
            // Calculate the position to align the bottom of the view with the bottom of the trigger
            let topPosition = rect.bottom - viewHeight;
            
            // Make sure the view doesn't go above the viewport
            if (topPosition < 0) {
              topPosition = 0;
            }
            
            viewRef.current.style.top = `${topPosition}px`;
            viewRef.current.style.left = `${rect.right + 8}px`;
          }
        }, 10);
      }
    };
    
    positionView();
    
    // In case the window is resized
    window.addEventListener('resize', positionView);
    return () => {
      window.removeEventListener('resize', positionView);
    };
  }, []);
  
  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside of the termsPrivacyView
      if (viewRef.current && !viewRef.current.contains(event.target) && 
          !event.target.closest('[data-terms-privacy-trigger]')) {
        onClose();
      }
    }
    
    // Use capture phase to ensure we get the event first
    document.addEventListener("mousedown", handleClickOutside, true);
    
    // Add escape key handler
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener("keydown", handleEscKey);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  // Legal sections
  const legalItems = [
    {
      title: 'Terms of Service',
      description: 'User agreements and conditions',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      ),
      path: '/legal/terms',
    },
    {
      title: 'Privacy Policy',
      description: 'How we handle your data',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
      ),
      path: '/legal/privacy',
    },
    {
      title: 'Data Use',
      description: 'Information on data practices',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
          <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
          <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
        </svg>
      ),
      path: '/legal/data-use',
    },
  ];

  return (
    <div 
      ref={viewRef}
      className="fixed w-52 bg-white dark:bg-dark-card rounded-md shadow-lg border border-gray-200 dark:border-dark-border overflow-hidden z-[100]"
      style={{
        // Initial position will be set by useEffect
        left: '-1000px', // Start off-screen until properly positioned
        top: '0'
      }} 
      data-terms-privacy-view
      onMouseEnter={(e) => {
        // Ensure the view stays visible when hovered
        e.stopPropagation();
      }}
      onMouseLeave={(e) => {
        const termsPrivacyTrigger = document.querySelector('[data-terms-privacy-trigger]');
        if (!termsPrivacyTrigger?.matches(':hover')) {
          onClose();
        }
      }}
    >
      {/* Content */}
      <div className="p-2">
        <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-300">Legal</div>
        <div className="space-y-0.5">
          {legalItems.map((item, index) => (
            <div 
              key={index}
              tabIndex={0}
              role="button"
              data-legal-item
              className="flex items-center p-1.5 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer"
              onClick={() => {
                router.push(item.path);
                onClose();
                // If a parent navigation handler is provided, call it to close the parent view
                if (onNavigate) onNavigate();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  router.push(item.path);
                  onClose();
                  // If a parent navigation handler is provided, call it to close the parent view
                  if (onNavigate) onNavigate();
                }
              }}
            >
              <div className="text-gray-500 dark:text-gray-400 mr-2">
                {item.icon}
              </div>
              <div className="text-xs text-gray-800 dark:text-gray-200">{item.title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
